<template>
  <div class="w-full bg-white">
    <!-- 1200px未満：現状レイアウト（大2/3＋小2枚 交互モザイク／変更なし） -->
    <div class="w-full xl:hidden">
      <!-- 3枚1組のグループ -->
      <template v-for="(group, groupIndex) in imageGroups" :key="groupIndex">
        <div class="flex flex-col md:flex-row">
          <!-- 大きい画像（PC: 偶数グループは左、奇数グループは右） -->
          <div :class="['w-full cursor-pointer overflow-hidden md:w-2/3', groupIndex % 2 === 0 ? 'md:order-1' : 'md:order-2']" @click="onClickImage(group.largeIndex)">
            <OptimizedImage
              loading="lazy"
              :alt="`gallery-image-${group.largeIndex}`"
              :class="['aspect-[3/2] h-full w-full object-cover object-center']"
              :image-url="imgUrls[group.largeIndex]"
              :optimized-image-options="{ width: 1280 }"
            />
          </div>
          <!-- 小さい画像2枚（PC: 縦積み、SP: 横並び） -->
          <div :class="['flex w-full flex-row md:w-1/3 md:flex-col', groupIndex % 2 === 0 ? 'md:order-2' : 'md:order-1']">
            <div v-for="smallIndex in group.smallIndices" :key="smallIndex" class="w-1/2 cursor-pointer overflow-hidden md:w-full" @click="onClickImage(smallIndex)">
              <OptimizedImage
                loading="lazy"
                :alt="`gallery-image-${smallIndex}`"
                :class="['aspect-[3/2] h-full w-full object-cover object-center']"
                :image-url="imgUrls[smallIndex]"
                :optimized-image-options="{ width: 560 }"
              />
            </div>
          </div>
        </div>
      </template>
      <!-- 余り画像（小画像サイズのグリッドで末尾配置。単独で大きく表示させない／モバイル2列・タブレット3列） -->
      <div v-if="remainingIndices.length > 0" class="grid grid-cols-2 md:grid-cols-3">
        <div v-for="index in remainingIndices" :key="index" class="cursor-pointer overflow-hidden" @click="onClickImage(index)">
          <OptimizedImage
            loading="lazy"
            :alt="`gallery-image-${index}`"
            :class="['aspect-[3/2] h-full w-full object-cover object-center']"
            :image-url="imgUrls[index]"
            :optimized-image-options="{ width: 560 }"
          />
        </div>
      </div>
    </div>
    <!-- 1200px以上：新レイアウト（大1枚[1/2]＋小4枚[2列×2段, 各1/4]／余白なし） -->
    <div class="hidden w-full xl:block">
      <!-- 5枚1組のグループ -->
      <template v-for="(group, groupIndex) in imageGroupsXl" :key="groupIndex">
        <div class="flex">
          <!-- 大きい画像（偶数グループは左、奇数グループは右） -->
          <div :class="['w-1/2 cursor-pointer overflow-hidden', groupIndex % 2 === 0 ? 'order-1' : 'order-2']" @click="onClickImage(group.largeIndex)">
            <OptimizedImage
              loading="lazy"
              :alt="`gallery-image-${group.largeIndex}`"
              :class="['aspect-[3/2] h-full w-full object-cover object-center']"
              :image-url="imgUrls[group.largeIndex]"
              :optimized-image-options="{ width: 960 }"
            />
          </div>
          <!-- 小さい画像4枚（2列×2段） -->
          <div :class="['grid w-1/2 grid-cols-2', groupIndex % 2 === 0 ? 'order-2' : 'order-1']">
            <div v-for="smallIndex in group.smallIndices" :key="smallIndex" class="cursor-pointer overflow-hidden" @click="onClickImage(smallIndex)">
              <OptimizedImage
                loading="lazy"
                :alt="`gallery-image-${smallIndex}`"
                :class="['aspect-[3/2] h-full w-full object-cover object-center']"
                :image-url="imgUrls[smallIndex]"
                :optimized-image-options="{ width: 480 }"
              />
            </div>
          </div>
        </div>
      </template>
      <!-- 余り画像（小画像サイズ[1/4]の4列グリッドで末尾配置。単独で大きく表示させない） -->
      <div v-if="remainingIndicesXl.length > 0" class="grid grid-cols-4">
        <div v-for="index in remainingIndicesXl" :key="index" class="cursor-pointer overflow-hidden" @click="onClickImage(index)">
          <OptimizedImage
            loading="lazy"
            :alt="`gallery-image-${index}`"
            :class="['aspect-[3/2] h-full w-full object-cover object-center']"
            :image-url="imgUrls[index]"
            :optimized-image-options="{ width: 480 }"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { OptimizedImage } from '@/wabunka-src/common/components';

interface Props {
  imgUrls: string[];
  onClickImage: (index: number) => void;
}
const props = defineProps<Props>();
const { imgUrls, onClickImage } = toRefs(props);

/**
 * 3枚1組のグループを作成
 * 例: 7枚の場合 → [{largeIndex: 0, smallIndices: [1,2]}, {largeIndex: 3, smallIndices: [4,5]}]
 */
const imageGroups = computed(() => {
  const groupCount = Math.floor(props.imgUrls.length / 3);
  return Array.from({ length: groupCount }, (_, i) => ({
    largeIndex: i * 3,
    smallIndices: [i * 3 + 1, i * 3 + 2],
  }));
});

/**
 * 3で割り切れない余り画像のインデックス
 * 例: 7枚の場合 → [6]、8枚の場合 → [6, 7]
 */
const remainingIndices = computed(() => {
  const remainder = props.imgUrls.length % 3;
  if (remainder === 0) return [];
  const startIndex = props.imgUrls.length - remainder;
  return Array.from({ length: remainder }, (_, i) => startIndex + i);
});

/**
 * 1200px以上用：5枚1組（大1枚＋小4枚）のグループを作成
 * 例: 7枚の場合 → [{largeIndex: 0, smallIndices: [1,2,3,4]}]
 */
const imageGroupsXl = computed(() => {
  const groupCount = Math.floor(props.imgUrls.length / 5);
  return Array.from({ length: groupCount }, (_, i) => ({
    largeIndex: i * 5,
    smallIndices: [i * 5 + 1, i * 5 + 2, i * 5 + 3, i * 5 + 4],
  }));
});

/**
 * 1200px以上用：5で割り切れない余り画像のインデックス
 * 例: 7枚の場合 → [5, 6]
 */
const remainingIndicesXl = computed(() => {
  const remainder = props.imgUrls.length % 5;
  if (remainder === 0) return [];
  const startIndex = props.imgUrls.length - remainder;
  return Array.from({ length: remainder }, (_, i) => startIndex + i);
});
</script>
